import React from "react";
import {
  Card as MuiCard,
  CardContent,
  CardActions,
  CardProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { colors, shadows, borderRadius } from "../../theme/tokens";

const StyledCard = styled(MuiCard)(({ theme }) => ({
  borderRadius: borderRadius.lg,
  boxShadow: shadows.base,
  border: `1px solid ${colors.neutral[200]}`,
  transition: "all 0.2s ease-in-out",
  overflow: "hidden",

  "&:focus-within": {
    outline: `2px solid ${colors.primary[500]}`,
    outlineOffset: "2px",
    boxShadow: shadows.md,
  },

  "&.interactive": {
    cursor: "pointer",
    "&:hover": {
      boxShadow: shadows.lg,
      transform: "translateY(-4px)",
      borderColor: colors.neutral[300],
    },
    "&:active": {
      transform: "translateY(-1px)",
      boxShadow: shadows.base,
    },
  },
}));

interface CustomCardProps extends Omit<CardProps, "elevation"> {
  children: React.ReactNode;
  interactive?: boolean;
  elevation?: "none" | "low" | "medium" | "high";
}

export const Card: React.FC<CustomCardProps> = ({
  children,
  interactive = false,
  elevation = "medium",
  className,
  ...props
}) => {
  const elevationStyles = {
    none: { boxShadow: "none" },
    low: { boxShadow: shadows.sm },
    medium: { boxShadow: shadows.base },
    high: { boxShadow: shadows.lg },
  };

  return (
    <StyledCard
      className={`${interactive ? "interactive" : ""} ${className || ""}`}
      sx={elevationStyles[elevation]}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

export { CardContent, CardActions };
